import React, { useState, memo } from "react";
import { Link } from "react-router-dom";
import { FaHeart, FaRegHeart, FaUserPlus, FaUserCheck, FaComment, FaGift, FaCamera, FaList, FaTh, FaInfoCircle, FaUserMinus } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const SkeletonItem = ({ viewMode }) => {
  return (
    <motion.div
      className={`bg-gray-100 rounded-lg shadow-md overflow-hidden ${
        viewMode === "list" ? "h-20 flex items-center" : viewMode === "gallery" ? "h-[360px] flex flex-col" : "h-[400px] flex flex-col"
      }`}
      initial={{ opacity: 0 }}
      animate={{
        opacity: [0.5, 1, 0.5],
        transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
      }}
      exit={{ opacity: 0 }}
    >
      {viewMode === "list" ? (
        <div className="flex items-center p-4">
          <div className="w-12 h-12 bg-gray-300 rounded-full mr-4 animate-pulse"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-300 rounded w-1/3 animate-pulse"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2 animate-pulse"></div>
          </div>
          <div className="flex space-x-2">
            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
          </div>
        </div>
      ) : viewMode === "gallery" ? (
        <div className="flex flex-col h-full">
          <div className="w-full h-[240px] bg-gray-300 rounded-t-lg animate-pulse"></div>
          <div className="p-4 flex-1">
            <div className="h-4 bg-gray-300 rounded w-3/4 mx-auto mb-2 animate-pulse"></div>
            <div className="h-3 bg-gray-300 rounded w-1/2 mx-auto mb-1 animate-pulse"></div>
            <div className="h-3 bg-gray-300 rounded w-1/3 mx-auto animate-pulse"></div>
          </div>
          <div className="flex justify-center space-x-2 mb-3 px-4">
            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col h-full p-6">
          <div className="flex items-center mb-4">
            <div className="w-16 h-16 bg-gray-300 rounded-full mr-4 animate-pulse"></div>
            <div className="flex-1 space-y-2">
              <div className="h-5 bg-gray-300 rounded w-1/2 animate-pulse"></div>
              <div className="h-4 bg-gray-300 rounded w-1/3 animate-pulse"></div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-300 rounded w-3/4 animate-pulse"></div>
            <div className="h-4 bg-gray-300 rounded w-2/3 animate-pulse"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2 animate-pulse"></div>
            <div className="h-4 bg-gray-300 rounded w-1/3 animate-pulse"></div>
          </div>
          <div className="flex justify-between mt-4 space-x-2">
            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

const SkeletonList = ({ viewMode, skeletonCount }) => {
  const skeletonItems = Array(skeletonCount).fill(null);
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {viewMode === "list" ? (
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead>
                <tr className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                  <th className="p-4 text-left font-semibold">Hình ảnh</th>
                  <th className="p-4 text-left font-semibold">Tên</th>
                  <th className="p-4 text-left font-semibold">Địa chỉ</th>
                  <th className="p-4 text-left font-semibold">Trạng thái</th>
                  <th className="p-4 text-left font-semibold">Hành động</th>
                </tr>
              </thead>
              <tbody>
                {skeletonItems.map((_, index) => (
                  <tr key={index} className="border-b">
                    <td className="p-4">
                      <SkeletonItem viewMode="list" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : viewMode === "gallery" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {skeletonItems.map((_, index) => (
              <SkeletonItem key={index} viewMode="gallery" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skeletonItems.map((_, index) => (
              <SkeletonItem key={index} viewMode="detail" />
            ))}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

const UserList = ({
  filteredUsers,
  authUser,
  likeStates,
  friendRequestStates,
  friendRequests,
  matches,
  friends,
  isLoadingLike,
  isLoadingFriend,
  handleLike,
  handleUnlike,
  sendFriendRequest,
  cancelFriendRequest,
  handleRespondFriendRequest,
  handleRespondMatchRequest,
  handleOpenChat,
  handleSendGift,
  handleViewPhotos,
  handleRemoveFriend,
  currentPage,
  totalPages,
  totalUsers,
  handlePageChange,
  showTooltip,
  loading,
  skeletonCount = 10,
  isFriendList = false,
  isFriendRequestList = false,
  isMatchRequestList = false,
}) => {
  const [viewMode, setViewMode] = useState("gallery");

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
      },
    },
    exit: { opacity: 0, y: -20 },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 rounded-lg ${
              viewMode === "list" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"
            } hover:bg-blue-400 transition duration-200`}
            title="Chế độ List"
          >
            <FaList />
          </button>
          <button
            onClick={() => setViewMode("gallery")}
            className={`p-2 rounded-lg ${
              viewMode === "gallery" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"
            } hover:bg-blue-400 transition duration-200`}
            title="Chế độ Gallery"
          >
            <FaTh />
          </button>
          <button
            onClick={() => setViewMode("detail")}
            className={`p-2 rounded-lg ${
              viewMode === "detail" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"
            } hover:bg-blue-400 transition duration-200`}
            title="Chế độ Detail"
          >
            <FaInfoCircle />
          </button>
        </div>
      </div>

      {loading ? (
        <SkeletonList
          viewMode={viewMode}
          skeletonCount={skeletonCount || Math.min(filteredUsers.length || 10, 10)}
        />
      ) : filteredUsers.length === 0 ? (
        <p className="text-center text-gray-500">Không tìm thấy người dùng nào.</p>
      ) : (
        <AnimatePresence mode="wait">
          {viewMode === "list" ? (
            <motion.div
              key={`list-${currentPage}`}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="overflow-x-auto"
            >
              <table className="w-full bg-white rounded-lg shadow-lg">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                    <th className="p-4 text-left font-semibold">Hình ảnh</th>
                    <th className="p-4 text-left font-semibold">Tên</th>
                    <th className="p-4 text-left font-semibold">Địa chỉ</th>
                    <th className="p-4 text-left font-semibold">Trạng thái</th>
                    <th className="p-4 text-left font-semibold">Hành động</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => {
                    const age = user.dateOfBirth
                      ? Math.floor(
                          (new Date() - new Date(user.dateOfBirth)) / (1000 * 60 * 60 * 24 * 365)
                        )
                      : null;
                    const isLiked = authUser && authUser._id && likeStates[user._id] === true;
                    const isFriendRequestPending =
                      authUser &&
                      authUser._id &&
                      friendRequestStates[user._id];
                    const isSentByCurrentUser = friendRequests.some(
                      (req) =>
                        String(req.senderId._id || req.senderId) === String(authUser._id) &&
                        String(req.receiverId._id || req.receiverId) === String(user._id) &&
                        req.status === "pending"
                    );
                    if (!user._id || !/^[0-9a-fA-F]{24}$/.test(user._id)) {
                      console.warn(`User ID không hợp lệ: ${user._id}`);
                      return null;
                    }
                    if (!user.membershipId) {
                      console.warn(`User ${user._id} is missing membershipId`);
                    }
                    return (
                      <motion.tr
                        key={user._id}
                        variants={itemVariants}
                        className="border-b hover:bg-gray-50 transition duration-200"
                      >
                        <td className="p-4">
                          <div className="relative w-12 h-12">
                            <img
                              src={
                                user?.primaryPhoto?.photoUrl ||
                                user?.mainImage ||
                                user?.avatar ||
                                "https://placehold.co/50"
                              }
                              alt={`${user.name}'s profile avatar`}
                              className="w-full h-full object-cover rounded-full shadow-lg border-2 border-gray-200"
                              onError={(e) => (e.target.src = "https://placehold.co/50")}
                              loading="lazy"
                            />
                            {user.isOnline && (
                              <span className="absolute bottom-0 right-0 w-5 h-5 bg-green-500 rounded-full border-2 border-white"></span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-gray-800">
                          <Link
                            to={`/profile/${user.membershipId}`}
                            className="text-blue-600 hover:underline"
                          >
                            {user.name}, {age || "N/A"}
                          </Link>
                        </td>
                        <td className="p-4 text-gray-600">
                          {user.locationText?.city || "N/A"}, {user.locationText?.country || "N/A"}
                        </td>
                        <td className="p-4 text-gray-600">
                          {user.isOnline ? (
                            <span className="text-green-600">Online</span>
                          ) : (
                            <span className="text-gray-500">Offline</span>
                          )}
                        </td>
                        <td className="p-4 flex space-x-2">
                          {isFriendRequestList ? (
                            <>
                              <button
                                onClick={() => handleRespondFriendRequest(user.requestId, "accept")}
                                className={`p-2 rounded-full bg-green-500 text-white hover:bg-green-600 ${
                                  isLoadingFriend ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                                disabled={isLoadingFriend}
                                title="Chấp nhận"
                              >
                                <FaUserCheck size={16} />
                              </button>
                              <button
                                onClick={() => handleRespondFriendRequest(user.requestId, "reject")}
                                className={`p-2 rounded-full bg-red-500 text-white hover:bg-red-600 ${
                                  isLoadingFriend ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                                disabled={isLoadingFriend}
                                title="Từ chối"
                              >
                                <FaUserMinus size={16} />
                              </button>
                            </>
                          ) : isMatchRequestList ? (
                            <>
                              <button
                                onClick={() => handleRespondMatchRequest(user.matchId, "accept")}
                                className={`p-2 rounded-full bg-green-500 text-white hover:bg-green-600 ${
                                  isLoadingLike ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                                disabled={isLoadingLike}
                                title="Chấp nhận"
                              >
                                <FaUserCheck size={16} />
                              </button>
                              <button
                                onClick={() => handleRespondMatchRequest(user.matchId, "reject")}
                                className={`p-2 rounded-full bg-red-500 text-white hover:bg-red-600 ${
                                  isLoadingLike ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                                disabled={isLoadingLike}
                                title="Từ chối"
                              >
                                <FaUserMinus size={16} />
                              </button>
                            </>
                          ) : isFriendList ? (
                            <>
                              <button
                                onClick={() => handleOpenChat(user._id)}
                                className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600"
                                title="Chat"
                              >
                                <FaComment size={16} />
                              </button>
                              <button
                                onClick={() => handleSendGift(user._id)}
                                className="p-2 rounded-full bg-purple-500 text-white hover:bg-purple-600"
                                title="Gửi quà"
                              >
                                <FaGift size={16} />
                              </button>
                              <button
                                onClick={() => handleViewPhotos(user)}
                                className="p-2 rounded-full bg-gray-500 text-white hover:bg-gray-600"
                                title={`Xem ảnh (${user.photos?.length || 0})`}
                              >
                                <FaCamera size={16} />
                              </button>
                              <button
                                onClick={() => handleRemoveFriend(user._id)}
                                className={`p-2 rounded-full bg-red-500 text-white hover:bg-red-600 ${
                                  isLoadingFriend ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                                disabled={isLoadingFriend}
                                title="Xóa bạn"
                              >
                                <FaUserMinus size={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={async () => {
                                  if (!user._id) {
                                    showTooltip("ID người dùng không hợp lệ!", "error");
                                    return;
                                  }
                                  try {
                                    if (isLiked) {
                                      await handleUnlike(user._id);
                                    } else {
                                      await handleLike(user._id);
                                    }
                                  } catch (err) {
                                    showTooltip(err.message || "Lỗi không xác định!", "error");
                                  }
                                }}
                                className={`p-2 rounded-full ${
                                  isLiked
                                    ? "bg-gray-400 text-white hover:bg-gray-500"
                                    : "bg-red-500 text-white hover:bg-red-600"
                                } ${isLoadingLike ? "opacity-50 cursor-not-allowed" : ""}`}
                                disabled={isLoadingLike}
                                title={isLiked ? "Bỏ thích" : "Thích"}
                              >
                                {isLiked ? <FaHeart size={16} /> : <FaRegHeart size={16} />}
                              </button>
                              <button
                                onClick={async () => {
                                  if (!user._id) {
                                    showTooltip("ID người dùng không hợp lệ!", "error");
                                    return;
                                  }
                                  try {
                                    if (isFriendRequestPending && isSentByCurrentUser) {
                                      await cancelFriendRequest(user._id);
                                    } else if (!isFriendRequestPending) {
                                      await sendFriendRequest(user._id);
                                    }
                                  } catch (err) {
                                    showTooltip(err.message || "Lỗi khi gửi yêu cầu kết bạn", "error");
                                  }
                                }}
                                className={`p-2 rounded-full ${
                                  isFriendRequestPending
                                    ? "bg-yellow-500 text-white hover:bg-yellow-600"
                                    : "bg-green-500 text-white hover:bg-green-600"
                                } ${
                                  isLoadingFriend || (isFriendRequestPending && !isSentByCurrentUser)
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                }`}
                                disabled={isLoadingFriend || (isFriendRequestPending && !isSentByCurrentUser)}
                                title={
                                  isFriendRequestPending
                                    ? isSentByCurrentUser
                                      ? "Hủy yêu cầu kết bạn"
                                      : "Đang chờ kết bạn"
                                    : "Kết bạn"
                                }
                              >
                                {isFriendRequestPending ? <FaUserCheck size={16} /> : <FaUserPlus size={16} />}
                              </button>
                              <button
                                onClick={() => handleOpenChat(user._id)}
                                className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600"
                                title="Chat"
                              >
                                <FaComment size={16} />
                              </button>
                              <button
                                onClick={() => handleSendGift(user._id)}
                                className="p-2 rounded-full bg-purple-500 text-white hover:bg-purple-600"
                                title="Gửi quà"
                              >
                                <FaGift size={16} />
                              </button>
                              <button
                                onClick={() => handleViewPhotos(user)}
                                className="p-2 rounded-full bg-gray-500 text-white hover:bg-gray-600"
                                title={`Xem ảnh (${user.photos?.length || 0})`}
                              >
                                <FaCamera size={16} />
                              </button>
                            </>
                          )}
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </motion.div>
          ) : viewMode === "gallery" ? (
            <motion.div
              key={`gallery-${currentPage}`}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4"
            >
              {filteredUsers.map((user) => {
                const age = user.dateOfBirth
                  ? Math.floor(
                      (new Date() - new Date(user.dateOfBirth)) / (1000 * 60 * 60 * 24 * 365)
                    )
                  : null;
                const isLiked = authUser && authUser._id && likeStates[user._id] === true;
                const isFriendRequestPending =
                  authUser &&
                  authUser._id &&
                  friendRequestStates[user._id];
                const isSentByCurrentUser = friendRequests.some(
                  (req) =>
                    String(req.senderId._id || req.senderId) === String(authUser._id) &&
                    String(req.receiverId._id || req.receiverId) === String(user._id) &&
                    req.status === "pending"
                );
                if (!user._id || !/^[0-9a-fA-F]{24}$/.test(user._id)) {
                  console.warn(`User ID không hợp lệ: ${user._id}`);
                  return null;
                }
                if (!user.membershipId) {
                  console.warn(`User ${user._id} is missing membershipId`);
                }
                return (
                  <motion.div
                    key={user._id}
                    variants={itemVariants}
                    className="relative bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 flex flex-col items-center overflow-hidden"
                  >
                    <div className="relative w-full h-[240px]">
                      <Link to={`/profile/${user.membershipId}`}>
                        <img
                          src={
                            user?.primaryPhoto?.photoUrl ||
                            user?.mainImage ||
                            user?.avatar ||
                            "https://placehold.co/180x240"
                          }
                          alt={`${user.name}'s profile avatar`}
                          className="w-full h-full object-cover rounded-t-lg shadow-sm cursor-pointer"
                          onError={(e) => (e.target.src = "https://placehold.co/180x240")}
                          loading="lazy"
                        />
                      </Link>
                      {user.isOnline && (
                        <span className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full border-2 border-white"></span>
                      )}
                    </div>
                    <div className="text-center px-4 py-3 w-full">
                      <Link to={`/profile/${user.membershipId}`}>
                        <p className="font-semibold text-base text-gray-800 truncate hover:text-blue-600 transition-colors">
                          {user.name}, {age || "N/A"}
                        </p>
                      </Link>
                      <p className="text-sm text-gray-600 truncate">
                        {user.locationText?.city || "N/A"}, {user.locationText?.country || "N/A"}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Tìm: {user.gender === "male" ? "Nam" : user.gender === "female" ? "Nữ" : "Khác"}
                      </p>
                      <p className="text-xs text-gray-500">
                        {user.isOnline ? (
                          <span className="text-green-600">Online</span>
                        ) : (
                          <span className="text-gray-500">Offline</span>
                        )}
                      </p>
                    </div>
                    <div className="flex justify-center space-x-2 mb-3 px-4">
                      {isFriendRequestList ? (
                        <>
                          <button
                            onClick={() => handleRespondFriendRequest(user.requestId, "accept")}
                            className={`p-2 rounded-full bg-green-500 text-white hover:bg-green-600 ${
                              isLoadingFriend ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            disabled={isLoadingFriend}
                            title="Chấp nhận"
                          >
                            <FaUserCheck size={16} />
                          </button>
                          <button
                            onClick={() => handleRespondFriendRequest(user.requestId, "reject")}
                            className={`p-2 rounded-full bg-red-500 text-white hover:bg-red-600 ${
                              isLoadingFriend ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            disabled={isLoadingFriend}
                            title="Từ chối"
                          >
                            <FaUserMinus size={16} />
                          </button>
                        </>
                      ) : isMatchRequestList ? (
                        <>
                          <button
                            onClick={() => handleRespondMatchRequest(user.matchId, "accept")}
                            className={`p-2 rounded-full bg-green-500 text-white hover:bg-green-600 ${
                              isLoadingLike ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            disabled={isLoadingLike}
                            title="Chấp nhận"
                          >
                            <FaUserCheck size={16} />
                          </button>
                          <button
                            onClick={() => handleRespondMatchRequest(user.matchId, "reject")}
                            className={`p-2 rounded-full bg-red-500 text-white hover:bg-red-600 ${
                              isLoadingLike ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            disabled={isLoadingLike}
                            title="Từ chối"
                          >
                            <FaUserMinus size={16} />
                          </button>
                        </>
                      ) : isFriendList ? (
                        <>
                          <button
                            onClick={() => handleOpenChat(user._id)}
                            className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600"
                            title="Chat"
                          >
                            <FaComment size={16} />
                          </button>
                          <button
                            onClick={() => handleSendGift(user._id)}
                            className="p-2 rounded-full bg-purple-500 text-white hover:bg-purple-600"
                            title="Gửi quà"
                          >
                            <FaGift size={16} />
                          </button>
                          <button
                            onClick={() => handleViewPhotos(user)}
                            className="p-2 rounded-full bg-gray-500 text-white hover:bg-gray-600"
                            title={`Xem ảnh (${user.photos?.length || 0})`}
                          >
                            <FaCamera size={16} />
                          </button>
                          <button
                            onClick={() => handleRemoveFriend(user._id)}
                            className={`p-2 rounded-full bg-red-500 text-white hover:bg-red-600 ${
                              isLoadingFriend ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            disabled={isLoadingFriend}
                            title="Xóa bạn"
                          >
                            <FaUserMinus size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={async () => {
                              if (!user._id) {
                                showTooltip("ID người dùng không hợp lệ!", "error");
                                return;
                              }
                              try {
                                if (isLiked) {
                                  await handleUnlike(user._id);
                                } else {
                                  await handleLike(user._id);
                                }
                              } catch (err) {
                                showTooltip(err.message || "Lỗi không xác định!", "error");
                              }
                            }}
                            className={`p-2 rounded-full ${
                              isLiked
                                ? "bg-gray-400 text-white hover:bg-gray-500"
                                : "bg-red-500 text-white hover:bg-red-600"
                            } ${isLoadingLike ? "opacity-50 cursor-not-allowed" : ""}`}
                            disabled={isLoadingLike}
                            title={isLiked ? "Bỏ thích" : "Thích"}
                          >
                            {isLiked ? <FaHeart size={16} /> : <FaRegHeart size={16} />}
                          </button>
                          <button
                            onClick={async () => {
                              if (!user._id) {
                                showTooltip("ID người dùng không hợp lệ!", "error");
                                return;
                              }
                              try {
                                if (isFriendRequestPending && isSentByCurrentUser) {
                                  await cancelFriendRequest(user._id);
                                } else if (!isFriendRequestPending) {
                                  await sendFriendRequest(user._id);
                                }
                              } catch (err) {
                                showTooltip(err.message || "Lỗi khi gửi yêu cầu kết bạn", "error");
                              }
                            }}
                            className={`p-2 rounded-full ${
                              isFriendRequestPending
                                ? "bg-yellow-500 text-white hover:bg-yellow-600"
                                : "bg-green-500 text-white hover:bg-green-600"
                            } ${
                              isLoadingFriend || (isFriendRequestPending && !isSentByCurrentUser)
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                            disabled={isLoadingFriend || (isFriendRequestPending && !isSentByCurrentUser)}
                            title={
                              isFriendRequestPending
                                ? isSentByCurrentUser
                                  ? "Hủy yêu cầu kết bạn"
                                  : "Đang chờ kết bạn"
                                : "Kết bạn"
                            }
                          >
                            {isFriendRequestPending ? <FaUserCheck size={16} /> : <FaUserPlus size={16} />}
                          </button>
                          <button
                            onClick={() => handleOpenChat(user._id)}
                            className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600"
                            title="Chat"
                          >
                            <FaComment size={16} />
                          </button>
                          <button
                            onClick={() => handleSendGift(user._id)}
                            className="p-2 rounded-full bg-purple-500 text-white hover:bg-purple-600"
                            title="Gửi quà"
                          >
                            <FaGift size={16} />
                          </button>
                          <button
                            onClick={() => handleViewPhotos(user)}
                            className="p-2 rounded-full bg-gray-500 text-white hover:bg-gray-600"
                            title={`Xem ảnh (${user.photos?.length || 0})`}
                          >
                            <FaCamera size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              key={`detail-${currentPage}`}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filteredUsers.map((user) => {
                const age = user.dateOfBirth
                  ? Math.floor(
                      (new Date() - new Date(user.dateOfBirth)) / (1000 * 60 * 60 * 24 * 365)
                    )
                  : null;
                const isLiked = authUser && authUser._id && likeStates[user._id] === true;
                const isFriendRequestPending =
                  authUser &&
                  authUser._id &&
                  friendRequestStates[user._id];
                const isSentByCurrentUser = friendRequests.some(
                  (req) =>
                    String(req.senderId._id || req.senderId) === String(authUser._id) &&
                    String(req.receiverId._id || req.receiverId) === String(user._id) &&
                    req.status === "pending"
                );
                if (!user._id || !/^[0-9a-fA-F]{24}$/.test(user._id)) {
                  console.warn(`User ID không hợp lệ: ${user._id}`);
                  return null;
                }
                if (!user.membershipId) {
                  console.warn(`User ${user._id} is missing membershipId`);
                }
                return (
                  <motion.div
                    key={user._id}
                    variants={itemVariants}
                    className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="flex items-center mb-4">
                      <div className="relative w-16 h-16 mr-4">
                        <img
                          src={
                            user?.primaryPhoto?.photoUrl ||
                            user?.mainImage ||
                            user?.avatar ||
                            "https://placehold.co/50"
                          }
                          alt={`${user.name}'s profile avatar`}
                          className="w-full h-full object-cover rounded-full shadow-lg border-2 border-gray-200"
                          onError={(e) => (e.target.src = "https://placehold.co/50")}
                          loading="lazy"
                        />
                        {user.isOnline && (
                          <span className="absolute bottom-0 right-0 w-6 h-6 bg-green-500 rounded-full border-2 border-white"></span>
                        )}
                      </div>
                      <div>
                        <Link to={`/profile/${user.membershipId}`}>
                          <h3 className="text-xl font-semibold text-gray-800 hover:text-blue-600 transition-colors">
                            {user.name}, {age || "N/A"}
                          </h3>
                        </Link>
                        <p className="text-gray-600">
                          {user.locationText?.city || "N/A"}, {user.locationText?.country || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2 text-gray-700">
                      <p>
                        <span className="font-medium">Ngày sinh:</span>{" "}
                        {user.dateOfBirth
                          ? new Date(user.dateOfBirth).toLocaleDateString("vi-VN")
                          : "Chưa cập nhật"}
                      </p>
                      <p>
                        <span className="font-medium">Giới tính:</span>{" "}
                        {user.gender === "male"
                          ? "Nam"
                          : user.gender === "female"
                          ? "Nữ"
                          : user.gender === "other"
                          ? "Khác"
                          : "Chưa cập nhật"}
                      </p>
                      <p>
                        <span className="font-medium">Tiểu sử:</span>{" "}
                        {user.bio || "Chưa cập nhật"}
                      </p>
                      <p>
                        <span className="font-medium">Trạng thái:</span>{" "}
                        {user.isOnline ? (
                          <span className="text-green-600">Online</span>
                        ) : (
                          <span className="text-gray-500">Offline</span>
                        )}
                      </p>
                    </div>
                    <div className="flex justify-between mt-4 space-x-2">
                      {isFriendRequestList ? (
                        <>
                          <button
                            onClick={() => handleRespondFriendRequest(user.requestId, "accept")}
                            className={`p-2 rounded-full bg-green-500 text-white hover:bg-green-600 ${
                              isLoadingFriend ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            disabled={isLoadingFriend}
                            title="Chấp nhận"
                          >
                            <FaUserCheck size={16} />
                          </button>
                          <button
                            onClick={() => handleRespondFriendRequest(user.requestId, "reject")}
                            className={`p-2 rounded-full bg-red-500 text-white hover:bg-red-600 ${
                              isLoadingFriend ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            disabled={isLoadingFriend}
                            title="Từ chối"
                          >
                            <FaUserMinus size={16} />
                          </button>
                        </>
                      ) : isMatchRequestList ? (
                        <>
                          <button
                            onClick={() => handleRespondMatchRequest(user.matchId, "accept")}
                            className={`p-2 rounded-full bg-green-500 text-white hover:bg-green-600 ${
                              isLoadingLike ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            disabled={isLoadingLike}
                            title="Chấp nhận"
                          >
                            <FaUserCheck size={16} />
                          </button>
                          <button
                            onClick={() => handleRespondMatchRequest(user.matchId, "reject")}
                            className={`p-2 rounded-full bg-red-500 text-white hover:bg-red-600 ${
                              isLoadingLike ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            disabled={isLoadingLike}
                            title="Từ chối"
                          >
                            <FaUserMinus size={16} />
                          </button>
                        </>
                      ) : isFriendList ? (
                        <>
                          <button
                            onClick={() => handleOpenChat(user._id)}
                            className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600"
                            title="Chat"
                          >
                            <FaComment size={16} />
                          </button>
                          <button
                            onClick={() => handleSendGift(user._id)}
                            className="p-2 rounded-full bg-purple-500 text-white hover:bg-purple-600"
                            title="Gửi quà"
                          >
                            <FaGift size={16} />
                          </button>
                          <button
                            onClick={() => handleViewPhotos(user)}
                            className="p-2 rounded-full bg-gray-500 text-white hover:bg-gray-600"
                            title={`Xem ảnh (${user.photos?.length || 0})`}
                          >
                            <FaCamera size={16} />
                          </button>
                          <button
                            onClick={() => handleRemoveFriend(user._id)}
                            className={`p-2 rounded-full bg-red-500 text-white hover:bg-red-600 ${
                              isLoadingFriend ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                            disabled={isLoadingFriend}
                            title="Xóa bạn"
                          >
                            <FaUserMinus size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={async () => {
                              if (!user._id) {
                                showTooltip("ID người dùng không hợp lệ!", "error");
                                return;
                              }
                              try {
                                if (isLiked) {
                                  await handleUnlike(user._id);
                                } else {
                                  await handleLike(user._id);
                                }
                              } catch (err) {
                                showTooltip(err.message || "Lỗi không xác định!", "error");
                              }
                            }}
                            className={`p-2 rounded-full ${
                              isLiked
                                ? "bg-gray-400 text-white hover:bg-gray-500"
                                : "bg-red-500 text-white hover:bg-red-600"
                            } ${isLoadingLike ? "opacity-50 cursor-not-allowed" : ""}`}
                            disabled={isLoadingLike}
                            title={isLiked ? "Bỏ thích" : "Thích"}
                          >
                            {isLiked ? <FaHeart size={16} /> : <FaRegHeart size={16} />}
                          </button>
                          <button
                            onClick={async () => {
                              if (!user._id) {
                                showTooltip("ID người dùng không hợp lệ!", "error");
                                return;
                              }
                              try {
                                if (isFriendRequestPending && isSentByCurrentUser) {
                                  await cancelFriendRequest(user._id);
                                } else if (!isFriendRequestPending) {
                                  await sendFriendRequest(user._id);
                                }
                              } catch (err) {
                                showTooltip(err.message || "Lỗi khi gửi yêu cầu kết bạn", "error");
                              }
                            }}
                            className={`p-2 rounded-full ${
                              isFriendRequestPending
                                ? "bg-yellow-500 text-white hover:bg-yellow-600"
                                : "bg-green-500 text-white hover:bg-green-600"
                            } ${
                              isLoadingFriend || (isFriendRequestPending && !isSentByCurrentUser)
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                            disabled={isLoadingFriend || (isFriendRequestPending && !isSentByCurrentUser)}
                            title={
                              isFriendRequestPending
                                ? isSentByCurrentUser
                                  ? "Hủy yêu cầu kết bạn"
                                  : "Đang chờ kết bạn"
                                : "Kết bạn"
                            }
                          >
                            {isFriendRequestPending ? <FaUserCheck size={16} /> : <FaUserPlus size={16} />}
                          </button>
                          <button
                            onClick={() => handleOpenChat(user._id)}
                            className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600"
                            title="Chat"
                          >
                            <FaComment size={16} />
                          </button>
                          <button
                            onClick={() => handleSendGift(user._id)}
                            className="p-2 rounded-full bg-purple-500 text-white hover:bg-purple-600"
                            title="Gửi quà"
                          >
                            <FaGift size={16} />
                          </button>
                          <button
                            onClick={() => handleViewPhotos(user)}
                            className="p-2 rounded-full bg-gray-500 text-white hover:bg-gray-600"
                            title={`Xem ảnh (${user.photos?.length || 0})`}
                          >
                            <FaCamera size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex justify-center items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded disabled:opacity-50 hover:bg-gray-300 transition duration-200"
          >
            Trước
          </button>
          <div className="flex space-x-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-3 py-1 rounded ${
                  currentPage === page
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                } transition duration-200`}
              >
                {page}
              </button>
            ))}
          </div>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded disabled:opacity-50 hover:bg-gray-300 transition duration-200"
          >
            Sau
          </button>
          <span className="text-gray-700">
            Trang {currentPage} / {totalPages} (Tổng: {totalUsers} người dùng)
          </span>
        </div>
      )}
    </>
  );
};

export default memo(UserList);